import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Globe, FlaskConical, Users, Handshake, Newspaper,
  BookOpen, MessageSquare, BarChart3, Image, Settings, LogOut, Home,
  ChevronLeft, Menu, FileText, GalleryVerticalEnd, Search, Layout, GraduationCap
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_df2c336a-cf24-4860-adef-adda9eebb723/artifacts/c9aru5vi_Color%20Logo.png";

const menuItems = [
  { id: "overview", label: "Tableau de bord", icon: LayoutDashboard },
  { type: "divider", label: "CONTENU DU SITE" },
  { id: "homepage", label: "Page d'accueil", icon: Layout },
  { id: "site-config", label: "Sections du site", icon: Globe },
  { id: "director", label: "Responsable", icon: GraduationCap },
  { id: "projects", label: "Projets", icon: FlaskConical },
  { id: "publications", label: "Publications", icon: FileText },
  { id: "team", label: "Équipe", icon: Users },
  { id: "partners", label: "Partenaires", icon: Handshake },
  { id: "news", label: "Actualités", icon: Newspaper },
  { id: "blog", label: "Blog", icon: BookOpen },
  { id: "gallery", label: "Galerie", icon: GalleryVerticalEnd },
  { type: "divider", label: "GESTION" },
  { id: "media", label: "Médias", icon: Image },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "stats", label: "Statistiques", icon: BarChart3 },
  { id: "seo", label: "SEO", icon: Search },
];

export default function AdminLayout({ activeTab, onTabChange, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full bg-white ${mobile ? "" : "border-r border-slate-100"}`}>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-100">
        <img src={LOGO_URL} alt="Logo" className="h-8 w-auto" />
        {(!collapsed || mobile) && (
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 font-['Quicksand'] truncate">UR-EBioReZ</p>
            <p className="text-[10px] text-slate-400">Administration</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {menuItems.map((item, i) => {
          if (item.type === "divider") {
            return (!collapsed || mobile) ? (
              <p key={`div-${item.label}`} className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400">{item.label}</p>
            ) : <div key={`div-${i}`} className="my-2 border-t border-slate-100" />;
          }
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => { onTabChange(item.id); if (mobile) setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-0.5 ${
                isActive
                  ? "bg-[#1670B9] text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-4.5 h-4.5 flex-shrink-0" />
              {(!collapsed || mobile) && <span className="truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User */}
      <div className="border-t border-slate-100 p-3 space-y-1.5">
        <button onClick={() => navigate("/")} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors" data-testid="admin-home-btn">
          <Home className="w-4 h-4" />
          {(!collapsed || mobile) && <span>Voir le site</span>}
        </button>
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" />
          {(!collapsed || mobile) && <span>Déconnexion</span>}
        </button>
        {(!collapsed || mobile) && (
          <div className="px-3 py-2">
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#FAFAFA] overflow-hidden" data-testid="admin-dashboard">
      {/* Desktop sidebar */}
      <div className={`hidden lg:flex flex-col transition-all duration-300 ${collapsed ? "w-16" : "w-64"}`}>
        <Sidebar />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-4 left-0 z-10 p-1.5 rounded-r-lg bg-white border border-l-0 border-slate-100 text-slate-400 hover:text-slate-600"
          style={{ left: collapsed ? "64px" : "256px", transition: "left 0.3s" }}
        >
          <ChevronLeft className={`w-3.5 h-3.5 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <div className="bg-white border-b border-slate-100 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-50">
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="font-['Quicksand'] text-lg font-semibold text-slate-800 capitalize">
              {menuItems.find(m => m.id === activeTab)?.label || "Administration"}
            </h1>
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </div>
      </div>
    </div>
  );
}
